import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, PenLine, AlertCircle } from 'lucide-react';
import type { Asset, FormSchema, IssuedKitItem, Station } from '@hcc/types';
import { conditionMet } from '@hcc/types';
import type { PersonApi } from '../../../hooks/usePeople';
import { useSubmitHandover } from '../../../hooks/useKitHandover';
import { useCurrentScope } from '../../../hooks/useMobilisations';
import {
  validateFormValues,
  isFieldVisible,
} from '../../onboarding/formfields/validateForm';
import type { FieldInvalid } from '../../onboarding/formfields/FieldView';
import type { FormValues } from '../../onboarding/formfields/formValues';
import { SignaturePadModal } from '../SignaturePadModal';
import { Button } from '../../ui/Button';
import { buildDeskPrefill } from './deskPrefill';
import {
  DeskFormField,
  type PendingDeskFile,
  type StoredDeskFile,
} from './DeskFormField';
import { ApiError } from '../../../lib/api';

interface HandoverFormModalProps {
  station: Station;
  person: PersonApi;
  formId: string;
  formTitle: string;
  schema: FormSchema;
  items: IssuedKitItem[];
  kitNotes: string;
  /** Full asset rows for tracked items, for prefill metadata. */
  assetsById: Record<string, Asset | undefined>;
  clientEventId: string;
  onClose: () => void;
  /** Called after a successful submit; the drawer closes itself. */
  onDone: () => void;
}

/**
 * The desk handover checklist: the operator and the driver complete the
 * configured form together, then the driver signs once. Submit writes the
 * kit issuance, the checklist proof event and the submission row atomically
 * server-side. No drafts: the whole handover happens in one sitting.
 */
export function HandoverFormModal({
  station,
  person,
  formId,
  formTitle,
  schema,
  items,
  kitNotes,
  assetsById,
  clientEventId,
  onClose,
  onDone,
}: HandoverFormModalProps) {
  const scope = useCurrentScope();
  // Server-side this is derived from the write scope; mirrored here so the
  // client validation agrees (fields flagged optional_on_event_day).
  const relaxEventDay = scope?.kind === 'mobilisation';

  const prefill = useMemo(
    () =>
      buildDeskPrefill(schema, {
        personName: person.full_name,
        items,
        assetsById,
        today: new Date().toISOString().slice(0, 10),
      }),
    // Prefill seeds once per modal open; the fields stay editable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [values, setValues] = useState<FormValues>(prefill);
  const [files, setFiles] = useState<PendingDeskFile[]>([]);
  const [stored, setStored] = useState<StoredDeskFile[]>([]);
  const [invalid, setInvalid] = useState<Record<string, FieldInvalid>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [signing, setSigning] = useState(false);

  const submit = useSubmitHandover(person.id);

  const fileMap = useMemo(() => {
    const m: Record<string, unknown[]> = {};
    for (const f of files) (m[f.field_key] ??= []).push(f);
    for (const s of stored) (m[s.field_key] ??= []).push(s);
    return m;
  }, [files, stored]);

  function validate() {
    return validateFormValues({
      schema,
      values,
      signatureFiles: fileMap,
      savedFiles: fileMap,
      priorFiles: {},
      consent: false,
      relaxEventDay,
    });
  }

  function onComplete() {
    const result = validate();
    setInvalid(result.invalid);
    setErrors(result.messages);
    if (result.messages.length === 0) setSigning(true);
  }

  async function onSignatureConfirm(dataUrl: string) {
    setSigning(false);
    try {
      await submit.mutateAsync({
        station,
        form_id: formId,
        values,
        files: files.map(({ field_key, filename, base64 }) => ({ field_key, filename, base64 })),
        stored_files: stored,
        items,
        ...(kitNotes.trim() ? { kit_notes: kitNotes.trim() } : {}),
        signature_png: dataUrl,
        client_event_id: clientEventId,
      });
      toast.success(`${station} handover recorded for ${person.full_name}`);
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not record the handover. Try again.');
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-fg/40 backdrop-blur-sm" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={formTitle}
        className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-6"
      >
        <div className="flex max-h-full w-full max-w-2xl flex-col rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
          <header className="flex items-start justify-between gap-3 border-b border-divider/10 p-4 sm:p-5">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-accent">
                {station} handover
              </div>
              <h2 className="mt-0.5 truncate text-lg font-semibold tracking-tight text-fg">
                {formTitle}
              </h2>
              <p className="text-xs text-fg-muted">
                {person.full_name} · complete together at the desk, then the driver signs.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-token-sm p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
            {/* System-known facts, recorded with the submission and stamped
                onto the signed PDF automatically. The form itself doesn't
                need Registration / Driver / Date fields. */}
            <div className="rounded-token-sm bg-accent/10 px-3 py-2 text-xs">
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-fg">
                <span>
                  <span className="font-semibold">Driver:</span> {person.full_name}
                </span>
                {items.length > 0 ? (
                  <span>
                    <span className="font-semibold">Issuing:</span>{' '}
                    {items
                      .map(
                        (i) =>
                          `${i.quantity > 1 ? `${i.quantity}x ` : ''}${i.name}${i.registration ? ` (${i.registration})` : ''}`,
                      )
                      .join(' · ')}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-fg-muted">
                Recorded automatically and printed on the signed PDF along with the date and
                vehicle details.
              </p>
            </div>

            {schema.pages
              .filter((pg) => conditionMet(pg.visible_if, values))
              .map((pg, pi) => (
                <section key={pi} className="space-y-4">
                  {pg.title ? (
                    <h3 className="border-b border-line/10 pb-1 text-sm font-semibold text-fg">
                      {pg.title}
                    </h3>
                  ) : null}
                  {pg.description ? (
                    <p className="text-sm text-fg-muted">{pg.description}</p>
                  ) : null}
                  {pg.fields
                    .filter((f) => isFieldVisible(f, values))
                    .map((f) => (
                      <DeskFormField
                        key={f.key}
                        field={f}
                        value={values[f.key] ?? null}
                        onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                        files={files.filter((x) => x.field_key === f.key)}
                        setFiles={(next) =>
                          setFiles((s) => [...s.filter((x) => x.field_key !== f.key), ...next])
                        }
                        stored={stored.filter((x) => x.field_key === f.key)}
                        setStored={(next) =>
                          setStored((s) => [...s.filter((x) => x.field_key !== f.key), ...next])
                        }
                        invalid={invalid[f.key]}
                        clearInvalid={() =>
                          setInvalid((m) => {
                            if (!m[f.key]) return m;
                            const nextInvalid = { ...m };
                            delete nextInvalid[f.key];
                            return nextInvalid;
                          })
                        }
                      />
                    ))}
                </section>
              ))}

            {errors.length > 0 ? (
              <ul className="list-disc space-y-0.5 rounded-token-sm border border-danger-soft/30 bg-danger-soft/15 p-3 pl-7 text-sm text-danger-soft-fg">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            ) : null}

            <div className="flex items-start gap-2 rounded-token-sm bg-surface-2 px-3 py-2 text-xs text-fg-muted">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Submitting records the checklist AND issues the {items.length}{' '}
                {items.length === 1 ? 'item' : 'items'} from the drawer in one signed step.
              </span>
            </div>
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 p-3 sm:p-4">
            <Button variant="secondary" size="md" onClick={onClose}>
              Back
            </Button>
            <Button
              variant="success"
              size="md"
              iconLeft={<PenLine className="h-4 w-4" />}
              loading={submit.isPending}
              onClick={onComplete}
            >
              Driver signs and completes
            </Button>
          </footer>
        </div>
      </div>

      <SignaturePadModal
        open={signing}
        title={`${person.full_name} - driver / engineer signature`}
        onClose={() => setSigning(false)}
        onConfirm={onSignatureConfirm}
      />
    </>
  );
}
